// @ts-strict-ignore
import pick from 'lodash/pick';
import {Component} from 'panel';
import {Location} from 'react-router';

import {Dashboard, DashboardCard, DashboardQueryOrigin} from 'common/report/dashboards/types';
import {AppMode} from 'apps/boards/src/models/enums';
import {DashboardCardResult, queryCard} from 'apps/boards/src/report-util/query';
import {isBrowseUrlData, parseDashboardUrlHashParams} from 'common/report/dashboards/url';
import {DashboardsContext} from 'apps/boards/src/util/context';
import {getChartOptions} from 'apps/boards/src/report-util';
import {DashboardSelectorController} from 'common/report/controllers/dashboard-selector-controller';
import {reporter} from 'common/report/error-reporting';
import {
  getActiveBoardFilters,
  getBoardFiltersFromPersisted,
  getDashboardCardParams,
  GetDashboardCardParamsOptions,
  waitForNecessaryResources,
} from 'apps/boards/src/dashboards-app/util';
import {IronContextRegistry} from 'common/report/context-registry';
import {DashboardCardId} from 'common/report/util/saved-entities/types';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {apiClient} from 'common/util/api-client';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2reactSnabbdomWrapper} from 'common/util/react-snabbdom-wrapper';
import {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {isDashboardCard} from 'common/report/dashboards/util';
import {fetchDisplayNameData} from 'common/report/display-name-registry';
import {dataGroupStore} from 'common/report/stores/data-groups';
import {syncReportPropertyValueMappings} from 'common/report/util/mappings';
import {AsyncCountingSemaphore} from 'common/util/async-counting-semaphore';

import {template} from './template';
import './index.styl';

const MAX_CONCURRENT_QUERIES = 2;

interface DigestModeState {
  cards: DashboardCard[];
  cardResults: Map<DashboardCardId, DashboardCardResult>;
  dashboard: Dashboard;
  dashboardFailed: boolean;
  dashboardLoaded: boolean;
  dashboardLoadingFinished: boolean;
}

type JsxProps = {
  props: {
    context: DashboardsContext;
    selectorController: DashboardSelectorController;
    location: Location;
  };
};

export class DigestMode extends Component<DigestModeState, EmptyObj, EmptyObj, EmptyObj, IronContextRegistry> {
  public declare context: DashboardsContext;
  public declare loadPromise: Promise<void>;
  public declare location: Location;
  public declare queryApiPool: AsyncCountingSemaphore;
  public declare selectorController: DashboardSelectorController;

  get config() {
    return {
      template,
      defaultState: {
        cards: [],
        cardResults: new Map<number, DashboardCardResult>(), // key is card ID
        dashboard: null,
        dashboardFailed: false,
        dashboardLoaded: false,
        dashboardLoadingFinished: false,
      },
      contexts: [
        `urlManager` as const,
        `currentProject` as const,
        `metrics` as const,
        `behaviors` as const,
        `i18n` as const,
      ],
      defaultContexts: {
        metrics: new MetricsResource(),
        behaviors: new BehaviorsResource(),
      },
      helpers: {
        getCardOptions: (card: DashboardCard) => {
          const cardResult = this.state.cardResults.has(card.id)
            ? this.state.cardResults.get(card.id)
            : {
                invalidBookmark: false,
                dataGroupNotSupported: false,
                notSupported: false,
                queryLastUpdated: ``,
                queryResponseTimeMs: 0,
                chartLoaded: false,
                chartDescription: ``,
                chartParams: null,
              };
          const chartOptions = getChartOptions({
            bookmark: card.bookmark,
            chartOptionsOverride: card.chart_options,
            metricsResource: this.getContext(`metrics`),
            behaviorsResource: this.getContext(`behaviors`),
            currentProject: this.getContext(`currentProject`).get()?.value,
          });

          const options = {
            chartOptions,
            dataGroups: this.context.dataGroups,
            enableCustomAlerts: false,
            hasEditPermissions: false,
            dashboardMode: AppMode.Digest,
            isTvMode: false,
            projectId: this.context.projectId,
            projectTimeZone: this.context.projectTimezone,
            projectAsOfDate: this.project?.projectAsOfDate ?? null,
            reportUrl: ``,
            timeFilter: null,
            UTCOffset: this.context.utcOffset,
          } as GetDashboardCardParamsOptions;
          const cardParams = getDashboardCardParams(card, cardResult, options);

          return {
            key: `card-${card.id}`,
            params: {
              elref: `digestCard`,
              card,
              bookmark: card.bookmark,
              chartParams: cardResult?.chartParams,
              globalBreakdowns: this.state.dashboard.breakdowns,
              globalFilters: getActiveBoardFilters(this.state.dashboard.filters),
              ...cardParams,
            },
          };
        },
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();

    if (!this.context) {
      throw new Error(`"context" is a required component prop`);
    }
    if (!this.selectorController) {
      throw new Error(`"selectorController" is a required component prop`);
    }
    this.selectorController.resetActiveDashboard();

    this.queryApiPool = new AsyncCountingSemaphore(MAX_CONCURRENT_QUERIES);

    // expose load promise for digest Puppeteer script to wait for
    this.loadPromise = this.loadDashboardAndCards();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  get project() {
    return this.getContext(`currentProject`).get().value;
  }

  async loadDashboardAndCards() {
    try {
      window.dispatchEvent(new Event(`tti:query_start`));
      this.update({dashboardLoaded: false});

      // ensure api client has parsed the url for the access token,
      // rather than assuming a fetch has been made at this point
      await apiClient.getAccessToken();

      // get selected dashboard's model controller if not already selected
      const {parsedData} = parseDashboardUrlHashParams(this.location.hash.substr(1));
      if (isBrowseUrlData(parsedData)) {
        throw new Error(`Unexpected browse params in digest mode`);
      }

      let dashboardController = this.selectorController.getActiveDashboardController();
      if (!dashboardController || dashboardController.getModel().id !== parsedData.dashboardId) {
        await this.selectorController.changeActiveDashboard(parsedData.dashboardId);
        dashboardController = this.selectorController.getActiveDashboardController();
      }

      // load dashboard's model controller if not already loaded
      if (!dashboardController.getLoaded()) {
        await dashboardController.loadModel();
      }

      const dashboard = dashboardController.getModel();
      if (dashboardController.getError()) {
        throw new Error(`error loading board`);
      }

      const cards: DashboardCard[] = (dashboard.cards || []).filter(isDashboardCard).slice(0, 8);

      const {pathname, search, hash} = this.location;

      this.update({cards, dashboard, dashboardLoaded: true});
      window.dispatchEvent(new Event(`tti:complete`));
      const {projectId, workspaceId} = this.context;
      await Promise.all([
        dataGroupStore.fetch({projectId, workspaceId}),
        syncReportPropertyValueMappings(projectId) as Promise<any>,
        ...(this.context.isStandalone ? [fetchDisplayNameData(projectId, workspaceId)] : []),
        this.context.loadUserInfo(),
      ]);
      await Promise.allSettled(
        cards.map((card) =>
          (async () => {
            const currentWorkspaceCard = this.convertToCurrentWorkspaceCard(card);
            await waitForNecessaryResources({
              metricsResource: this.getContext(`metrics`),
              behaviorsResource: this.getContext(`behaviors`),
              bookmark: card.bookmark,
            });

            // Acquiring an already-waiting key in queryApiPool is not allowed (leads to deadlocks),
            // so abort any pending queryApiPool slots right before attempting to acquire().
            this.queryApiPool.abortPendingKey(card.id);
            if (await this.queryApiPool.acquire(card.id)) {
              try {
                // Assign digest requests a proper internal source to distinguish them from user client requests.
                const overrideHeaders: Record<string, string> = {
                  'Internal-Source': `ui.dashboard-digest`,
                };
                const cardResult = await queryCard({
                  card: currentWorkspaceCard,
                  currentProject: this.getContext(`currentProject`).get()?.value,
                  dashboard,
                  location: {
                    pathname,
                    search,
                    hash,
                    key: ``,
                    state: ``,
                  },
                  globalFilters: getBoardFiltersFromPersisted(dashboard.filters),
                  globalBreakdowns: dashboard.breakdowns,
                  timeFilterOverride: dashboard.time_filter,
                  useQueryCache: true, // TODO: use cache to until we reduce the DQS CPU consumption of digest retries.
                  abortSignal: null,
                  queryOverride: null,
                  queryOrigin: DashboardQueryOrigin.Digest,
                  overrideHeaders,
                  metricsResource: this.getContext(`metrics`),
                  behaviorsResource: this.getContext(`behaviors`),
                });
                this.state.cardResults.set(card.id, cardResult);
              } catch (err) {
                const responseBody = await err.response?.json();
                this.state.cardResults.set(card.id, {
                  invalidBookmark: false,
                  dataGroupNotSupported: false,
                  notSupported: false,
                  queryLastUpdated: ``,
                  queryResponseTimeMs: 0,
                  chartLoaded: false,
                  chartParams: null,
                  timeoutError: err.response && err.response.status === 504,
                  chartError: responseBody
                    ? {...pick(responseBody, [`error`, `message`, `data`]), status: err.response.status}
                    : {error: `Unexpected error`},
                });

                reporter.error(err);
              } finally {
                this.queryApiPool.release();
              }
            }
            this.update();
          })(),
        ),
      );
      this.update({
        dashboardLoadingFinished: true,
      });
    } catch (err) {
      window.dispatchEvent(new Event(`tti:invalid`));
      this.update({dashboardFailed: true});
      reporter.error(err);
    }
  }

  get urlManager() {
    return this.getContext(`urlManager`);
  }

  private convertToCurrentWorkspaceCard(card) {
    if (!this.context.workspaceId) {
      return card;
    } else {
      const currentWorkspaceCard = Object.assign({}, card);
      currentWorkspaceCard.bookmark.workspace_id = this.context.workspaceId;
      return currentWorkspaceCard;
    }
  }
}

export const DigestModeEl = defineAndMakeJsxFactory<JsxProps>(`digest-mode`, DigestMode);
export const DigestModeReact = panel2reactSnabbdomWrapper<JsxProps>({
  jsxFactory: DigestModeEl,
  requiredProps: [`context`, `selectorController`],
});
